import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Badge,
  Box,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ImageIcon from "@mui/icons-material/ImageOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import { useTranslation } from "react-i18next";
import { getMessages, sendMessage, uploadAttachment, markRead } from "../../api/chatService";
import { useBusinessMessages } from "../../context/BusinessMessagesContext";
import { formatDate } from "../Admin/constants";

const THREAD_POLL_INTERVAL_MS = 3000;

function formatRelativeTime(value, t) {
  if (!value) return "";
  const diffMs = Date.now() - new Date(value).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return t("justNow");
  if (diffMin < 60) return t("minutesAgo", { count: diffMin });
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return t("hoursAgo", { count: diffHours });
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return t("daysAgo", { count: diffDays });
  return new Date(value).toLocaleDateString();
}

export default function Messages() {
  const { t } = useTranslation(["boMessages", "boSidebar"]);
  // Matches backend MessageType enum (Models/Enums.cs): Text=1, Image=2, ProductLink=3, Video=4, OrderLink=5, Location=6.
  const MESSAGE_TYPE_PREVIEWS = {
    2: t("photo"),
    3: t("sharedProduct"),
    4: t("video"),
    5: t("sharedOrder"),
    6: t("sharedLocation"),
  };

  function lastMessagePreview(conversation) {
    if (conversation.lastMessageText) return conversation.lastMessageText;
    if (conversation.lastMessageType) return MESSAGE_TYPE_PREVIEWS[conversation.lastMessageType] || "...";
    return t("noMessagesYet");
  }

  const { conversations, refresh } = useBusinessMessages();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [threadError, setThreadError] = useState("");
  const [threadLoading, setThreadLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [sending, setSending] = useState(false);

  const threadPollRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadMessages = useCallback((conversationId) => {
    return getMessages(conversationId, 1, 50).then((data) => {
      setMessages([...data.items].reverse());
    });
  }, []);

  const handleSelectConversation = useCallback((conversationId) => {
    setMessages([]);
    setDraft("");
    setPendingFile(null);
    setThreadError("");
    setSelectedId(conversationId);
  }, []);

  const requestedConversationId = searchParams.get("conversationId");
  useEffect(() => {
    if (!requestedConversationId) return;
    const id = Number(requestedConversationId);
    if (conversations.some((c) => c.conversationId === id)) {
      handleSelectConversation(id);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedConversationId, conversations]);

  useEffect(() => {
    if (!selectedId) return undefined;

    setThreadLoading(true);
    setThreadError("");
    loadMessages(selectedId)
      .catch(() => setThreadError(t("boMessages:loadMessagesError")))
      .finally(() => setThreadLoading(false));

    markRead(selectedId).then(refresh).catch(() => {});

    threadPollRef.current = setInterval(() => {
      loadMessages(selectedId)
        .then(() => markRead(selectedId))
        .then(refresh)
        .catch(() => {});
    }, THREAD_POLL_INTERVAL_MS);

    return () => clearInterval(threadPollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, loadMessages, refresh]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ block: "end" });
  }, [messages]);

  const handleSend = async () => {
    const text = draft.trim();
    if ((!text && !pendingFile) || sending || !selectedId) return;

    setSending(true);
    try {
      if (pendingFile) {
        const message = await uploadAttachment(selectedId, pendingFile, text || undefined);
        setMessages((prev) => [...prev, message]);
        setPendingFile(null);
      } else {
        const message = await sendMessage(selectedId, { messageText: text });
        setMessages((prev) => [...prev, message]);
      }
      setDraft("");
      refresh();
    } catch {
      setThreadError(t("boMessages:sendError"));
    } finally {
      setSending(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setPendingFile(file);
    e.target.value = "";
  };

  const selectedConversation = conversations.find((c) => c.conversationId === selectedId);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {t("boSidebar:messages")}
      </Typography>

      <Stack direction="row" spacing={2} sx={{ height: "calc(100vh - 220px)", minHeight: 480 }}>
        <Paper sx={{ width: 320, flexShrink: 0, overflowY: "auto" }}>
          <List disablePadding>
            {conversations.length === 0 && (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {t("boMessages:noConversations")}
                </Typography>
              </Box>
            )}
            {conversations.map((conversation) => (
              <ListItemButton
                key={conversation.conversationId}
                selected={conversation.conversationId === selectedId}
                onClick={() => handleSelectConversation(conversation.conversationId)}
                alignItems="flex-start"
              >
                <ListItemAvatar>
                  <Badge color="error" badgeContent={conversation.unreadCount} invisible={!conversation.unreadCount}>
                    <Avatar src={conversation.customerProfileImageUrl || undefined}>
                      {conversation.customerName?.[0]?.toUpperCase()}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={conversation.customerName}
                  secondary={
                    <>
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {conversation.storeName}
                      </Typography>
                      <Typography component="span" variant="body2" color="text.secondary" noWrap sx={{ display: "block" }}>
                        {conversation.lastMessageIsMine ? t("boMessages:youPrefix") : ""}
                        {lastMessagePreview(conversation)}
                      </Typography>
                      <Typography component="span" variant="caption" color="text.secondary">
                        {formatRelativeTime(conversation.lastMessageAt, t)}
                      </Typography>
                    </>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>

        <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {!selectedId ? (
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <ChatBubbleOutlineIcon sx={{ fontSize: 40, color: "text.disabled" }} />
              <Typography variant="body2" color="text.secondary">
                {t("selectConversation")}
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
                <Typography variant="subtitle1">{selectedConversation?.customerName}</Typography>
                {selectedConversation?.storeName && (
                  <Typography variant="caption" color="text.secondary">
                    {selectedConversation.storeName}
                  </Typography>
                )}
              </Box>

              <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
                {threadError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {threadError}
                  </Alert>
                )}
                {threadLoading && messages.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t("loadingMessages")}
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {messages.map((message) => (
                      <Box
                        key={message.messageId}
                        sx={{
                          alignSelf: message.isMine ? "flex-end" : "flex-start",
                          maxWidth: "70%",
                        }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 1.5,
                            py: 1,
                            bgcolor: message.isMine ? "primary.main" : "grey.100",
                            color: message.isMine ? "primary.contrastText" : "text.primary",
                            borderRadius: 2,
                          }}
                        >
                          {message.isDeleted ? (
                            <Typography variant="body2" fontStyle="italic">
                              {t("messageDeleted")}
                            </Typography>
                          ) : (
                            <>
                              {message.attachmentUrl && message.attachmentFileType?.startsWith("image") && (
                                <Box
                                  component="img"
                                  src={message.attachmentUrl}
                                  alt="attachment"
                                  sx={{ maxWidth: 220, borderRadius: 1, mb: message.messageText ? 1 : 0, display: "block" }}
                                />
                              )}
                              {message.messageText ? (
                                <Typography variant="body2">{message.messageText}</Typography>
                              ) : (
                                !message.attachmentUrl && (
                                  <Typography variant="body2" fontStyle="italic">
                                    {MESSAGE_TYPE_PREVIEWS[message.messageType] || t("messageFallback")}
                                  </Typography>
                                )
                              )}
                            </>
                          )}
                        </Paper>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                          {formatDate(message.sentAt)}
                        </Typography>
                      </Box>
                    ))}
                    <div ref={messagesEndRef} />
                  </Stack>
                )}
              </Box>

              <Box sx={{ p: 1.5, borderTop: 1, borderColor: "divider" }}>
                {pendingFile && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    {t("fileReady", { name: pendingFile.name })}
                  </Typography>
                )}
                <Stack direction="row" spacing={1} alignItems="flex-end">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                  <IconButton onClick={() => fileInputRef.current?.click()} disabled={sending}>
                    <ImageIcon />
                  </IconButton>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    maxRows={4}
                    placeholder={pendingFile ? t("captionPlaceholder") : t("messagePlaceholder")}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                  />
                  <IconButton color="primary" onClick={handleSend} disabled={sending || (!draft.trim() && !pendingFile)}>
                    <SendIcon />
                  </IconButton>
                </Stack>
              </Box>
            </>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}
